import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useAuth } from '../../context/auth-context';
import { authFetch } from '../../utils/auth-fetch';
import { toast } from '../../components/ui/use-toast';
import { Absent } from './opsConsoleAbsent';
import OpsAlertsPanel, { DELIVERY_ALERT_KINDS } from './OpsAlertsPanel';
import OpsBatchesPanel from './OpsBatchesPanel';
import {
  RefreshCw,
  AlertTriangle,
  Store,
  Activity,
  Euro,
  Languages,
  ArrowUpDown,
  X,
  Blocks,
  Moon,
} from 'lucide-react';

/**
 * Shopify Ops Console — superadmin, read-only fleet backoffice
 * (PRD 2026-08-21 v5.2). A FLEET screen: deliberately not wired to the
 * global SiteContext — it spans every merchant, and the drill-down is keyed
 * by platform connection, not by the selected site.
 *
 * Absent-not-blank (A1.2): a field whose source has no value renders the
 * <Absent/> em-dash with a "no source" tooltip — NEVER 0 and never an empty
 * cell that reads as zero. Zeroes on this screen are measured zeroes.
 */

interface LocaleAggregate {
  completed: number;
  inFlight: number;
  failed: number;
  oldestInFlightAt: string | null;
  unpublishedCompleted: number;
}

interface MerchantRow {
  platform: string;
  connectionId: string;
  siteId: string;
  shopDomain: string | null;
  connectionStatus: string;
  connectedAt: string | null;
  disconnectedAt: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  consecutiveFailures: number;
  syncFailing: boolean;
  subscription: {
    subscriptionId: string;
    status: string;
    tierHandle: string;
    tierName: string;
    monthlyPriceEur: number | null;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    pagesOptimizedThisPeriod: number;
    pagesOptimizedCap: number | null;
    indexedSlotsCap: number | null;
  } | null;
  contributesMrr: boolean;
  counts: {
    products: number;
    generated: number;
    approved: number;
    rejected: number;
    awaitingReview: number;
    inProgress: number;
    otherStates: number;
    live: number;
  } | null;
  activeLocales: string[] | null;
  localeAggregates: Record<string, LocaleAggregate> | null;
  indexedSlotsHeld: number | null;
  parkedSlots: number | null;
  firstGenerationAt: string | null;
  activatedNotTranslated: number | null;
  translated: number | null;
  translationDenominator: number | null;
  /** Legal record. null = no acceptance on file for this shop — never "unknown date". */
  termsAcceptance: {
    acceptedAt: string;
    termsVersion: string;
    platformUserId: string | null;
  } | null;
  /**
   * hasIntegration and connected are BOTH here on purpose: an integration that exists but is
   * expired or revoked is actionable, and "no Google" is not. Collapsing them into one boolean
   * would hide the only one of the two an operator can do something about.
   */
  googleIntegration: {
    hasIntegration: boolean;
    connected: boolean;
    status: string | null;
    email: string | null;
    connectedAt: string | null;
    services: {
      gsc: { connected: boolean; propertyUrl: string | null };
      ga4: { connected: boolean; propertyId: string | null };
      ads: { connected: boolean; customerId: string | null };
      mc: { connected: boolean; merchantId: string | null };
    };
  } | null;
}

/**
 * The overview shape the SERVER derives (fleetDerivations.buildOverview) and
 * the shape `summarize` below reproduces for a SELECTION. They are asserted
 * deep-equal for the unfiltered fleet in OpsConsole.selection.test.tsx — the
 * only thing standing between "the tiles follow the filters" and a second
 * arithmetic that agrees today.
 */
interface FleetOverview {
  stores: { total: number; byStatus: Record<string, number> };
  merchantsByTier: { byTier: Record<string, number>; withSubscription: number; total: number };
  catalog: {
    products: number; generated: number; approved: number; rejected: number;
    // The segment the shipped funnel left invisible: 20 published + 5
    // pending_review = 25 generated, and only the 20 and the 25 were on
    // screen. Sourced from shopifyCoverageCounts.fetchGenerationPartition
    // like every other number here (A1.3) — never a console-local count.
    awaitingReview: number; inProgress: number; otherStates: number;
    live: number;
    measuredMerchants: number; totalMerchants: number;
  };
  dormancy: { dormant: number; measuredLiveStores: number; liveStores: number };
  translation: {
    translated: number | null; denominator: number | null;
    merchantsWithActiveLanguages: number; totalMerchants: number;
  };
  mrr: {
    amount: number; currency: string; payingMerchants: number;
    liveConnections: number; payingSyncFailing: number;
  };
}

interface FleetSignals {
  // Populations as ID LISTS, computed server-side. The client filters by set
  // membership and never restates a predicate — `status === 'active'`
  // reimplemented in TypeScript is the divergence class R1 names, arriving on
  // the side of the wire no query-spy test can reach.
  liveStores: string[];
  deadConnections: string[];
  dormantStores: string[];
  // Finding 13: renamed from `stalledCoverage` so the payload, the chip and
  // the table column are ONE word for one measure.
  untranslatedLanguages: string[];
  // The population that signal can fire on at all — so its zero can say
  // whether it is "nothing wrong" or "nothing measured".
  translationSurface: string[];
  revenueConcentration: {
    shopDomain: string | null; connectionId: string; amount: number; share: number;
  } | null;
}

interface FleetPayload {
  platform: string;
  platformDisplayName: string;
  mrr_label: string;
  generated_at: string;
  overview: FleetOverview;
  merchants: MerchantRow[];
  signals: FleetSignals;
}

interface ActivityPayload {
  historyBegins: string | null;
  events: Array<{
    type: string;
    at: string;
    provenance: 'derived' | 'ledger';
    connectionId: string;
    shopDomain: string | null;
    tier?: string;
    fromTier?: string;
    toTier?: string;
    // Both prices, so "Upgraded"/"Downgraded" is read from data rather than
    // guessed from tier names (tier order is not derivable from a handle).
    fromMonthlyPriceEur?: number | null;
    toMonthlyPriceEur?: number | null;
  }>;
}

/**
 * How a writeback state should READ — decided by the platform adapter, never
 * here (see shopifyOpsAdapter's writeback-vocabulary block). Exactly one kind,
 * `failed`, means something went wrong; `deliberate` covers the states someone
 * chose on purpose (a cancelled write, a declined one), which is the whole
 * point of finding 12: `skipped: 152` was reading as 152 failures.
 */
type WritebackKind = 'failed' | 'settled' | 'inFlight' | 'deliberate' | 'unknown';

interface WritebackState {
  status: string;
  operation: string;
  count: number;
  lastAttemptedAt: string | null;
  meaning: { kind: WritebackKind; label: string; why: string };
}

/**
 * One bulk-activation job, normalized by the adapter into TWO SEPARATE
 * FAMILIES of counter (finding 11):
 *   - the *Sources fields count PRODUCTS in the job;
 *   - `gate` counts CONTENT ITEMS published or withheld by the publication
 *     gate — one product publishes several, and the counter accumulates per
 *     translation-completion cycle.
 * Mixing them is the bug this shape exists to make unspellable: there is no
 * bare `published` in the source namespace to reach for by mistake.
 */
interface BulkActivationJob {
  id: string;
  locale: string;
  status: string;
  totalSources: number;
  queuedSources: number;
  queueFailedSources: number;
  /** null until the job's final per-product accounting is written (A1.2). */
  publishedSources: number | null;
  delayedSources: number | null;
  removedSources: number | null;
  /**
   * The MERCHANT-FACING denominator — the bulk-activation service's own `m`
   * (published + delayed), with removed products excluded because there was
   * nothing to publish for them. This, not `totalSources`, is what the
   * completion email quotes and therefore what an operator reconciles against.
   */
  publishableSources: number | null;
  gate: {
    published: number; skippedRejected: number; skippedInactive: number; skippedEntitlement: number;
  };
  /**
   * Whether the per-product accounting is a number, is coming, or will NEVER
   * arrive — decided by the adapter, because "which statuses still write
   * accounting" is platform lifecycle knowledge. A 'superseded' job is
   * terminal with none, and telling its reader to wait for one is the same
   * false reassurance as the fraction this card came to fix.
   */
  accounting: 'written' | 'pending' | 'never' | 'unknown';
  createdAt: string | null;
  completedAt: string | null;
}

interface MerchantDetail {
  merchant: MerchantRow;
  subscriptionHistory: Array<{
    subscriptionId: string; status: string; tierHandle: string;
    monthlyPriceEur: number | null; platformSubscriptionId: string | null;
    activatedAt: string | null; cancelledAt: string | null; createdAt: string | null;
  }>;
  removals: Array<{ id: string; source_id: string; note: string | null; actor: string; created_at: string }>;
  writeback: WritebackState[];
  /**
   * The individual rows behind the aggregate above, each with the exact error the platform
   * returned. Selected by HAVING an error rather than by a status list, so a `retrying` row that
   * carries a real rejection is not hidden behind an in-flight label.
   */
  writebackFailures: Array<{
    contentType: string;
    status: string;
    operation: string;
    attemptedAt: string | null;
    errorMessage: string;
    platformEntityId: string | null;
    sourceId: string | null;
    meaning: { kind: WritebackKind; label: string; why: string };
  }>;
  /** Capped list; this is the true count, so the card can say what it is not showing. */
  writebackFailuresTotal: number;
  bulkActivationJobs: BulkActivationJob[];
  /** Every list carries its denominator too: the card is capped, and says so. */
  bulkActivationJobsTotal: number;
  // Connection-resolved across the supersession chain (Phase 0 usage-key
  // rule): days before the last plan change stay keyed to historical
  // subscription rows, so each day carries the tier it occurred under.
  dailyUsage: Array<{ usage_date: string; chatbot_sessions_count: number; tier_handle: string }>;
}

const PLATFORM = 'shopify';

// How many activity entries render. Stated on screen when it BITES: a list
// silently cut at fifty is the same defect class as a blank that reads as a
// zero — the reader cannot tell "that is all there is" from "that is all we
// showed you".
const ACTIVITY_LIMIT = 50;


const fmtInt = (v: number) => v.toLocaleString();
const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleDateString() : null);
/** "3 Mar 2026" — the readable form the activity list states inline. */
const fmtDateMedium = (v: string | null) => (v
  ? new Date(v).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  : null);
const fmtDateTime = (v: string | null) => (v ? new Date(v).toLocaleString() : null);
const fmtEur = (v: number) => `€${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Renders `value / denominator` — every total carries its denominator (A2.2). */
function WithDenominator({ value, denominator, absentWhy }: {
  value: number | null; denominator: number | null; absentWhy?: string;
}) {
  if (value === null || value === undefined) return <Absent why={absentWhy} />;
  return (
    <span>
      {fmtInt(value)}
      {denominator === null || denominator === undefined
        ? <span title="No denominator source"> / <Absent why={absentWhy} /></span>
        : <span className="text-muted-foreground"> / {fmtInt(denominator)}</span>}
    </span>
  );
}

/**
 * ⛔ THE DRILL-DOWN PAYLOAD CONTRACT, CHECKED (review finding, 2026-09-04).
 *
 * The FLEET path already refuses a payload whose population lists are missing,
 * because defaulting them would render a confident, fully-populated screen
 * that is silently wrong. The drill-down had no equivalent — and this PR
 * changed its shape twice (`writeback[].meaning` added; `bulkActivationJobs`
 * reshaped from raw snake_case rows to a nested camelCase one).
 *
 * A blue-green window serving this bundle against the previous backend would
 * therefore have thrown a bare `TypeError: Cannot read properties of undefined
 * (reading 'kind')` out of the render and blanked the drawer, instead of the
 * named, actionable error its sibling was deliberately given. Same class of
 * defect, opposite corner of the file.
 *
 * This is NOT a fallback: nothing is defaulted, coerced or hidden. It converts
 * a render-time crash into the sentence that says which field is missing.
 */
function assertDrilldownShape(data: unknown): asserts data is MerchantDetail {
  const d = data as Partial<MerchantDetail> | null;
  const missing: string[] = [];
  if (!Array.isArray(d?.writeback)) missing.push('writeback');
  else if (!d.writeback.every((w) => w && typeof w.meaning?.kind === 'string')) {
    missing.push('writeback[].meaning');
  }
  if (!Array.isArray(d?.bulkActivationJobs)) missing.push('bulkActivationJobs');
  else if (!d.bulkActivationJobs.every((j) => j && j.gate && typeof j.accounting === 'string'
    && 'publishableSources' in j)) {
    missing.push('bulkActivationJobs[].gate/.accounting/.publishableSources');
  }
  if (typeof d?.bulkActivationJobsTotal !== 'number') missing.push('bulkActivationJobsTotal');
  // The failure list is how an operator reads the exact platform error without opening a database,
  // so an older backend that cannot supply it must say so rather than render an empty list that
  // reads as "no failures" — the precise inversion this card exists to prevent.
  if (!Array.isArray(d?.writebackFailures)) missing.push('writebackFailures');
  if (typeof d?.writebackFailuresTotal !== 'number') missing.push('writebackFailuresTotal');
  if (missing.length > 0) {
    throw new Error(
      `Drill-down payload is missing ${missing.join(', ')} — this build reads the normalized `
      + 'shape the platform adapter returns and will not guess it. The admin build and the backend '
      + 'are probably out of step.'
    );
  }
}

/**
 * ── WRITEBACK STATES (evaluation finding 12 — 2026-09-04) ─────────────────
 *
 * The card used to render `{operation} · {status}: {count}` in source order,
 * which put `set · skipped: 152` next to `set · success: 154` and invited the
 * reading that 152 writes had failed. Two changes, both here:
 *
 *   1. FAILURES FIRST and in red. Previously nothing distinguished a genuine
 *      `failed` row from a deliberate `skipped` one — same weight, same
 *      colour, alphabetical order. Now exactly one kind is red-with-triangle,
 *      and it sorts to the top; a deliberate skip is muted and sorts last.
 *   2. The LABEL comes from the adapter, so `skipped` renders as
 *      "skipped (cancelled or no longer written)" and stops reading as a
 *      failure count on its own.
 *
 * `unknown` shares the alarm styling deliberately: a status this console
 * cannot classify must not be quietly reassuring.
 */
const WRITEBACK_KIND_CLASS: Record<WritebackKind, string> = {
  failed: 'text-red-600',
  unknown: 'text-amber-600',
  inFlight: 'text-amber-600',
  settled: '',
  deliberate: 'text-muted-foreground',
};

const WRITEBACK_KIND_ORDER: WritebackKind[] = ['failed', 'unknown', 'inFlight', 'settled', 'deliberate'];

const ALARMING_WRITEBACK_KINDS: WritebackKind[] = ['failed', 'unknown'];

/** Actionable first, deliberate last; stable within a kind. */
const orderWriteback = (rows: WritebackState[]): WritebackState[] =>
  [...rows].sort((a, b) =>
    WRITEBACK_KIND_ORDER.indexOf(a.meaning.kind) - WRITEBACK_KIND_ORDER.indexOf(b.meaning.kind));

/**
 * ── ONE BULK-ACTIVATION JOB (evaluation finding 11 — 2026-09-04) ──────────
 *
 * ⛔ THE NUMERATOR IS `publishedSources`, NEVER `gate.published`. The shipped
 * line rendered `{gate_published}/{total_sources} published`, and on P4 that
 * produced `es · 23/2 published` and `fr · 0/2 published` for two jobs that
 * had BOTH published 2 of 2. The `fr` reading is the dangerous one: 23/2 is
 * visibly nonsense so a reader distrusts it, while 0/2 is entirely plausible
 * and sends someone investigating a failure that never happened.
 *
 * The gate figures are kept — they carry real information — but on their own
 * line, in their own unit, with no denominator to be mistaken for a fraction
 * of the job. They are shown only when the gate actually recorded something:
 * `0 content items published` beside `2 / 2 products published` would rebuild
 * the same confusion out of two honest numbers.
 *
 * Queue/shortfall counters ride along for the same reason the fraction was
 * wrong: `2 / 2 published` while sources sit delayed or failed to queue would
 * be a true number telling an untrue story.
 */
/**
 * Why the numerator is absent — one sentence per lifecycle answer, because
 * "not recorded YET" is a promise, and for a superseded job it is a false one.
 */
const ACCOUNTING_ABSENT_WHY: Record<BulkActivationJob['accounting'], string> = {
  written: 'Recorded.',
  pending: 'Per-product accounting is written when the job finishes, and this job is still running. It is not zero — it has not been recorded yet.',
  never: 'This job was closed out terminally WITHOUT per-product accounting — a later language activation superseded it, and the new interval\'s job owns the language from there. No number is coming; do not wait for one.',
  unknown: 'This job is in a status the console does not recognise, so whether accounting is still coming cannot be determined here. Check the bulk-activation service before drawing a conclusion.',
};

function BulkActivationJobLine({ job }: { job: BulkActivationJob }) {
  const gateRecorded = job.gate.published + job.gate.skippedRejected
    + job.gate.skippedInactive + job.gate.skippedEntitlement > 0;

  const shortfall: string[] = [];
  if (job.delayedSources !== null && job.delayedSources > 0) {
    shortfall.push(`${fmtInt(job.delayedSources)} delayed`);
  }
  if (job.queueFailedSources > 0) {
    shortfall.push(`${fmtInt(job.queueFailedSources)} failed to queue`);
  }

  return (
    <li className="space-y-0.5" data-testid={`bulk-job-${job.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span><b>{job.locale}</b> · {job.status}</span>
        <span>
          products published:{' '}
          <WithDenominator
            value={job.publishedSources}
            denominator={job.publishableSources}
            absentWhy={ACCOUNTING_ABSENT_WHY[job.accounting]}
          />
        </span>
        {job.completedAt && (
          <span className="text-muted-foreground">completed {fmtDate(job.completedAt)}</span>
        )}
      </div>

      {job.publishedSources === null && (
        <div className="text-xs text-muted-foreground"
          title={job.accounting === 'pending'
            ? 'How far the queueing pass got. It is progress, not an outcome — a queued source has not published yet.'
            : 'How far the queueing pass had got when the job stopped. It is not progress — nothing further will happen on this job.'}>
          {job.accounting === 'pending' ? 'queued so far: ' : 'queued when it stopped: '}
          <WithDenominator value={job.queuedSources} denominator={job.totalSources} />
        </div>
      )}

      {shortfall.length > 0 && (
        <div className="text-xs text-amber-600 flex items-center gap-1"
          title="Delayed sources are ours to fix: the translation or its publication did not land, and pipeline reconciliation may still heal them. Sources that failed to queue never entered translation at all.">
          <AlertTriangle className="h-3 w-3" />{shortfall.join(' · ')}
        </div>
      )}

      {/* Renders exactly when the fraction's denominator is NOT the job's own
          source total — which is the only time the two can be confused, and
          covers both removed products and any unaccounted remainder. */}
      {job.publishableSources !== null && job.publishableSources !== job.totalSources && (
        <div className="text-xs text-muted-foreground" data-testid={`bulk-job-${job.id}-reconcile`}
          title="The fraction above counts only the products this job could publish — the merchant-facing figure its completion email quotes. Products removed from the storefront while the job ran are excluded from it, because there was nothing to publish for them; they remain part of the job's own source total, which is why the two numbers differ.">
          {job.removedSources !== null && job.removedSources > 0
            ? `${fmtInt(job.removedSources)} removed from the storefront · `
            : ''}
          the job began with {fmtInt(job.totalSources)} sources; {fmtInt(job.publishableSources)} could be published
        </div>
      )}

      {gateRecorded && (
        <div className="text-xs text-muted-foreground"
          title="Publication-gate outcomes are CONTENT ITEMS, not products: one product publishes several items, and the counter accumulates across translation-completion cycles. There is no count of content items in the job, so these figures have no denominator — they are never a fraction of the products above.">
          publication gate, in content items:{' '}
          {fmtInt(job.gate.published)} published
          {job.gate.skippedRejected > 0 && ` · ${fmtInt(job.gate.skippedRejected)} skipped: source rejected or removed`}
          {job.gate.skippedInactive > 0 && ` · ${fmtInt(job.gate.skippedInactive)} skipped: language not active`}
          {job.gate.skippedEntitlement > 0 && ` · ${fmtInt(job.gate.skippedEntitlement)} skipped: plan does not cover it`}
        </div>
      )}
    </li>
  );
}

/**
 * ── THE SELECTION (evaluation findings 1, 2, 5, 10 — 2026-09-04) ──────────
 *
 * The shipped console filtered only the merchant TABLE, so three tiles
 * counted the estate's history (uninstalled stores included) while MRR
 * counted its present, with nothing on screen saying which was which. It got
 * worse with every uninstall.
 *
 * The fix is one selection that drives the WHOLE screen:
 *   - population: Live (the default) or All-including-uninstalled;
 *   - signals: dead/dying, dormant, untranslated languages — STACKABLE with
 *     AND semantics, so Live + Dead/dying is "the live merchants whose sync is
 *     broken", which is the actionable set rather than a lens you switch
 *     between.
 *
 * ⛔ Every predicate is a SET MEMBERSHIP test against an id list the server
 * computed. Nothing here re-derives "live" or "dormant" — see FleetSignals.
 */
type Population = 'live' | 'all';
type SignalKey = 'dead' | 'dormant' | 'untranslated';

/**
 * ── STATE THE POPULATION, NOT JUST THE DENOMINATOR (finding 13) ───────────
 *
 * `population` names the FleetSignals id list a signal can fire on AT ALL, so
 * its zero can distinguish "nothing wrong" from "nothing measured" — the
 * failure that killed the trial-expiring signal in PRD v5.1, and the one that
 * fails in the direction nobody investigates.
 *
 * It is optional on purpose. `Dead / dying` ranges over every connection in
 * the selection, so its fraction would be N/N — which finding 10 already
 * established says nothing — and `Dormant` publishes its measured denominator
 * in the dormancy tile beside it. This chip is the one with a population that
 * is neither everything nor already on screen.
 */
type PopulationIdList = 'translationSurface';

const SIGNAL_CHIPS: Array<{
  key: SignalKey;
  label: string;
  why: string;
  population?: { idList: PopulationIdList; are: string; none: string };
}> = [
  {
    key: 'dead',
    label: 'Dead / dying',
    why: 'Connection revoked, expired or in error, OR a sync that is failing.',
  },
  {
    key: 'dormant',
    label: 'Dormant',
    why: 'Live connection that has generated nothing at all.',
  },
  {
    // Not "Stalled coverage" (abstract, and over-claims: the signal catches
    // translations genuinely in flight as well as work that silently stopped)
    // and not "Translations pending" (names only the benign half, so the half
    // that matters would read as normal). "Untranslated languages" names the
    // UNIT, and is the same word the table column uses for the same measure.
    key: 'untranslated',
    label: 'Untranslated languages',
    why: 'A merchant with approved content and at least one active language that has no completed translation — whether the translation is still running or silently stopped. A merchant that activated a language before approving anything is NOT counted: it has nothing to translate yet, which is why this chip states the population it ranges over.',
    population: {
      idList: 'translationSurface',
      are: 'merchants with approved content and an active language',
      none: 'nothing measured — no merchant in this selection has approved content and an active language',
    },
  },
];

/**
 * The selection in the words the screen uses for it, written ONCE — the
 * summary sentence and the empty state both read from here, so the two can
 * never describe the same filters differently.
 */
function describeSelection(population: Population, activeSignals: SignalKey[]): string {
  const base = population === 'live'
    ? 'live connections only'
    : 'every connection, including uninstalled and pending';
  if (activeSignals.length === 0) return base;
  const named = activeSignals
    .map((k) => SIGNAL_CHIPS.find((c) => c.key === k)!.label.toLowerCase())
    .join(' and ');
  return `${base}, and ${named}`;
}

/**
 * ── A FILTER THAT MATCHES NOTHING IS NOT APPLIED (finding 14) ─────────────
 *
 * The chip read `Untranslated languages (0 of 1) · merchants with approved
 * content and an active language` — a HEALTHY zero, measured over a real
 * population. Clicking it emptied the selection, and the same chip then read
 * `(0 of 0) · nothing measured`, because every figure on the screen —
 * including the population line — re-described the empty set. Truthful in
 * isolation; it turned a healthy zero into an apparent dead check, which is
 * precisely the reading the population line was added to prevent.
 *
 * So the click is refused. Two things make that safe rather than a new way to
 * mislead:
 *
 * ⛔ A REFUSED FILTER MUST STILL SAY WHERE ITS MATCHES ARE (review finding,
 * 2026-09-04). Refusing the click removes the reader's only way to interrogate
 * the zero, so a SELECTION-RELATIVE zero would start reading as an ABSOLUTE
 * one — finding 13's exact failure, one level up. `Dead / dying (0)` under
 * Live, on a fleet whose two dead connections are both uninstalled, is a
 * different fact from `Dead / dying (0)` on a fleet with nothing dead
 * anywhere. So the chip carries its fleet-wide count when the two differ, and
 * the two states get two different sentences.
 *
 * ⛔ NEVER A FILTER THAT IS ALREADY ON. Its count inside its own selection is
 * legitimately zero whenever two individually non-empty chips have an empty
 * intersection, and refusing it there would strand the reader in the empty
 * state with no way back to where they came from. Selected always stays
 * clickable — the user must be able to back out of any state they reached.
 *
 * ⛔ `aria-disabled`, NOT the native `disabled` attribute (review finding,
 * 2026-09-04). A natively disabled button cannot take focus, so a keyboard or
 * screen-reader user tabs straight past it and never learns the filter is
 * there, let alone why it is unavailable — and `disabled:pointer-events-none`
 * also suppressed the very `title` meant to explain it. Measured on this
 * component before the change: focus never landed on the chip. `aria-disabled`
 * keeps the control focusable and announces its state, the reason rides in the
 * accessible NAME so assistive tech actually reads it, and the click is
 * refused HERE, in one place, so the control is genuinely inert rather than
 * merely styled.
 */
const POPULATION_NO_MATCH = 'No merchant in the current selection belongs to this population, so switching '
  + 'to it would empty the screen rather than narrow it. The count on each population button says where '
  + 'the matches are.';

/**
 * Two states, two sentences — never one sentence with a number swapped. A
 * zero that is this selection's and a zero that is the fleet's are different
 * facts, and the whole point of the change is that they must not read alike.
 */
const signalUnavailableWhy = (fleetWide: number) => (fleetWide > 0
  ? `This zero is THIS SELECTION'S, not the fleet's: ${fleetWide} merchant${fleetWide === 1 ? '' : 's'} `
    + `in the fleet match${fleetWide === 1 ? 'es' : ''} it, and none of them is in the current selection. `
    + 'Applying the filter here would empty the screen, so it is unavailable — widen the selection '
    + '(switch population, or drop one of the other filters) to reach them.'
  : 'No merchant in the fleet matches it at all, so there is nothing to filter to. This zero is the '
    + "fleet's, not just this selection's.");

function FilterButton({ selected, unavailable, title, ariaLabel, onClick, className, testId, children }: {
  selected: boolean;
  unavailable: boolean;
  title: string;
  /** The accessible name while unavailable — where the reason actually gets announced. */
  ariaLabel: string;
  onClick: () => void;
  className?: string;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      size="sm"
      variant={selected ? 'default' : 'outline'}
      aria-disabled={unavailable || undefined}
      aria-label={unavailable ? ariaLabel : undefined}
      // The refusal lives here, not in the DOM's disabled attribute — see the
      // block above. One choke point, so no caller can forget it.
      onClick={() => { if (unavailable) return; onClick(); }}
      title={title}
      className={[className, unavailable ? 'opacity-50 cursor-not-allowed' : null].filter(Boolean).join(' ') || undefined}
      data-testid={testId}
    >
      {children}
    </Button>
  );
}

const sumField = (rows: MerchantRow[], pick: (r: MerchantRow) => number | null | undefined) =>
  rows.reduce((sum, r) => {
    const v = pick(r);
    return v === null || v === undefined ? sum : sum + v;
  }, 0);

/**
 * summarize — the overview for an arbitrary SELECTION of merchant rows.
 *
 * A deliberate mirror of the server's fleetDerivations.buildOverview, and the
 * mirroring is enforced rather than trusted: OpsConsole.selection.test.tsx
 * requires this to be deep-equal to the server's own overview for the
 * unfiltered fleet, importing the real backend module to compare against.
 *
 * It exists client-side because the alternative — refetching per chip click —
 * would compute each view in a DIFFERENT snapshot, and a reader comparing
 * "Live" against "All" would be comparing two moments (A2.4). One snapshot,
 * many views, is the honest shape.
 *
 * NO COUNT IS COMPUTED HERE. Every number is a sum of per-merchant figures
 * the shared modules produced (A1.3/A2.3); the populations are id lists.
 */
export function summarize(
  rows: MerchantRow[],
  liveIds: Set<string>,
  dormantIds: Set<string>
): FleetOverview {
  const byStatus: Record<string, number> = {};
  for (const r of rows) byStatus[r.connectionStatus] = (byStatus[r.connectionStatus] || 0) + 1;

  const byTier: Record<string, number> = {};
  let withSubscription = 0;
  for (const r of rows) {
    if (r.subscription) {
      withSubscription += 1;
      byTier[r.subscription.tierHandle] = (byTier[r.subscription.tierHandle] || 0) + 1;
    }
  }

  const withCounts = rows.filter((r) => r.counts);
  const withTranslation = rows.filter((r) => r.translated !== null && r.translated !== undefined);
  const contributing = rows.filter((r) => r.contributesMrr);
  const liveConnections = rows.filter((r) => liveIds.has(r.connectionId));
  const measuredLiveStores = liveConnections.filter((r) => r.counts);

  return {
    stores: { total: rows.length, byStatus },
    merchantsByTier: { byTier, withSubscription, total: rows.length },
    catalog: {
      products: sumField(withCounts, (r) => r.counts!.products),
      generated: sumField(withCounts, (r) => r.counts!.generated),
      approved: sumField(withCounts, (r) => r.counts!.approved),
      rejected: sumField(withCounts, (r) => r.counts!.rejected),
      awaitingReview: sumField(withCounts, (r) => r.counts!.awaitingReview),
      inProgress: sumField(withCounts, (r) => r.counts!.inProgress),
      otherStates: sumField(withCounts, (r) => r.counts!.otherStates),
      live: sumField(withCounts, (r) => r.counts!.live),
      measuredMerchants: withCounts.length,
      totalMerchants: rows.length,
    },
    dormancy: {
      dormant: measuredLiveStores.filter((r) => dormantIds.has(r.connectionId)).length,
      measuredLiveStores: measuredLiveStores.length,
      liveStores: liveConnections.length,
    },
    translation: {
      translated: withTranslation.length > 0 ? sumField(withTranslation, (r) => r.translated) : null,
      denominator: withTranslation.length > 0
        ? sumField(withTranslation, (r) => r.translationDenominator)
        : null,
      merchantsWithActiveLanguages: withTranslation.length,
      totalMerchants: rows.length,
    },
    mrr: {
      amount: sumField(contributing, (r) => (r.subscription ? r.subscription.monthlyPriceEur : null)),
      currency: 'EUR',
      payingMerchants: contributing.length,
      liveConnections: liveConnections.length,
      payingSyncFailing: contributing.filter((r) => r.syncFailing).length,
    },
  };
}

/**
 * ── CYCLE POSITION (finding 6) ────────────────────────────────────────────
 *
 * `Optimized / cap` is billing-cycle-relative and resets invisibly, and every
 * merchant runs on their own clock — so 12/50 on day 2 and 12/50 on day 28
 * mean opposite things and the row showed no way to tell them apart. The
 * window was already in the payload and rendered only in the drill-down.
 *
 * "day N of M" rather than "days remaining": days-remaining alone is not
 * readable without the period LENGTH, which varies (28–31 days, and a Shopify
 * period need not be a calendar month). One phrase carries position and
 * length, which is what makes the two 12/50s distinguishable at a glance.
 *
 * Returns null for every input it cannot place — an absent bound, an
 * unparseable one, an inverted window, or an unusable snapshot time. The
 * caller renders <Absent/> rather than inventing a position (A1.2).
 */
type Cycle =
  | { state: 'in_period'; day: number; length: number }
  | { state: 'ended' }
  | { state: 'not_started' };

function cyclePosition(startIso: string | null, endIso: string | null, now: number): Cycle | null {
  if (!startIso || !endIso || !Number.isFinite(now)) return null;
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  // An inverted or degenerate window is a data anomaly, not a position: it is
  // reported as unplaceable rather than rendered as day 1 of 1.
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  const DAY = 24 * 60 * 60 * 1000;
  const length = Math.max(1, Math.round((end - start) / DAY));
  // A period whose end has passed is a REAL signal, not a rendering edge
  // case: the counter beside it belongs to a window that has closed, so the
  // ratio is stale until the next sync re-keys it.
  if (now >= end) return { state: 'ended' };
  // ⛔ AND A WINDOW THAT HAS NOT OPENED YET IS THE SAME KIND OF SIGNAL — this
  // is the branch a clamped `Math.max(1, …)` would have swallowed, printing
  // "day 1 of 30" for a cycle beginning in a fortnight while the counter
  // beside it still belongs to the PREVIOUS window. Forward-dated periods do
  // occur (written ahead on a plan change, or clock skew against Shopify).
  if (now < start) return { state: 'not_started' };
  const day = Math.min(length, Math.floor((now - start) / DAY) + 1);
  return { state: 'in_period', day, length };
}

/**
 * The cycle position, rendered under the ratio it qualifies. Absent bounds
 * render as absent — a position cannot be invented from a missing window.
 */
function CyclePosition({ start, end, now }: { start: string | null; end: string | null; now: number }) {
  const cycle = cyclePosition(start, end, now);
  if (!cycle) {
    return (
      <div className="text-xs">
        <Absent why="No usable billing period — the cycle position cannot be placed, so this ratio has no timeframe" />
      </div>
    );
  }
  // NOT named `window` — that shadows the global, which is the kind of
  // shadowing that reads fine until someone adds a `window.` call below it.
  const periodTitle = `Billing period ${fmtDateMedium(start)} → ${fmtDateMedium(end)}. The counter resets at period end.`;
  if (cycle.state === 'ended') {
    return (
      <div className="text-xs text-amber-600" title={`${periodTitle} This period has already ended — the ratio belongs to a closed window until the next sync re-keys it.`}>
        period ended {fmtDateMedium(end)}
      </div>
    );
  }
  if (cycle.state === 'not_started') {
    return (
      <div className="text-xs text-amber-600" title={`${periodTitle} This period has not started yet — the counter beside it belongs to the previous window, not this one.`}>
        period starts {fmtDateMedium(start)}
      </div>
    );
  }
  return (
    <div className="text-xs text-muted-foreground" title={periodTitle}>
      day {cycle.day} of {cycle.length}
    </div>
  );
}

/**
 * One sentence per activity entry, worded so it cannot overclaim.
 *
 *  - STATE-shaped for the connection columns: `connected_at` /
 *    `disconnected_at` are single mutable columns, so the honest reading is
 *    "this is where the store stands", not "this is an event we recorded".
 *    The date lives inside the sentence for exactly this reason — a date rail
 *    beside a bare verb reads as a log line, which is the claim being avoided.
 *  - EVENT-shaped for the subscription history rows, which really are
 *    append-only and really do record a transition.
 *  - "Upgraded" / "Downgraded" comes from the two prices on the event. Where
 *    either is absent, neither word is claimed and it is a plain change.
 */
function describeActivity(e: ActivityPayload['events'][number]): string {
  const on = fmtDateMedium(e.at);
  switch (e.type) {
    case 'install':
      return `Connected since ${on}`;
    case 'uninstall':
      return `Disconnected since ${on}`;
    case 'first_generation':
      // Also a current-state reading: MIN(created_at) over the rows that
      // exist now, which moves if the earliest generation is ever removed.
      return `Earliest generated content on record ${on}`;
    case 'subscription_started':
      return `Subscription recorded on ${e.tier} — ${on}`;
    case 'tier_change': {
      const from = e.fromMonthlyPriceEur;
      const to = e.toMonthlyPriceEur;
      const verb = from === null || from === undefined || to === null || to === undefined || from === to
        ? 'Changed plan'
        : (to > from ? 'Upgraded' : 'Downgraded');
      return `${verb} ${e.fromTier} → ${e.toTier} — ${on}`;
    }
    default:
      return `${e.type} — ${on}`;
  }
}

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  revoked: 'bg-red-100 text-red-800',
  expired: 'bg-red-100 text-red-800',
  error: 'bg-red-100 text-red-800',
  disconnected: 'bg-gray-200 text-gray-700',
};

/**
 * Badge ORDER is display state, not data: an object's key order is the order
 * merchant rows happened to arrive in, so the same fleet could render its
 * badges differently after a re-sort. Statuses follow the vocabulary order of
 * STATUS_BADGE — READ from it, not restated beside it, so a status added
 * there cannot silently sort itself to the end. Tiers go biggest-first.
 * An unknown status sorts last rather than being dropped.
 */
const sortedStatusEntries = (byStatus: Record<string, number>) => {
  const order = Object.keys(STATUS_BADGE);
  const rank = (k: string) => (order.indexOf(k) < 0 ? order.length : order.indexOf(k));
  return Object.entries(byStatus).sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
};
const sortedTierEntries = (byTier: Record<string, number>) =>
  Object.entries(byTier).sort(([a, na], [b, nb]) => nb - na || a.localeCompare(b));

type SortKey = 'products' | 'generated' | 'approved' | 'rejected' | 'shop' | 'tier' | 'price' | 'installed';

export default function OpsConsole() {
  const { token } = useAuth();
  const [fleet, setFleet] = useState<FleetPayload | null>(null);
  const [activity, setActivity] = useState<ActivityPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Default sort: product count, descending (PRD §5.2).
  const [sortKey, setSortKey] = useState<SortKey>('products');
  const [sortDesc, setSortDesc] = useState(true);
  // LIVE IS THE DEFAULT VIEW (Pau, 2026-09-04). The estate's history stays one
  // click away rather than being the thing you are shown without asking.
  const [population, setPopulation] = useState<Population>('live');
  const [activeSignals, setActiveSignals] = useState<SignalKey[]>([]);
  // ⛔ THE DRAWER IS DRIVEN BY THE SELECTION, NOT BY WHICHEVER RESPONSE LANDED
  // LAST (codex #5674, found independently by both reviewers). Every async
  // detail/placement request is bound to the connection it was issued for:
  //   - `activeConnectionRef` is the CURRENT selection, read at the async
  //     boundary — a response whose connection is no longer selected is
  //     discarded rather than rendered under another merchant's name;
  //   - an AbortController per selection actually cancels the in-flight
  //     request when the selection changes or the drawer closes, so a slow
  //     response for A cannot resolve into B's panel;
  //   - `selectedConnectionId` (not `detail`) gates rendering, which is what
  //     makes "closed during flight stays closed" structural rather than
  //     careful.
  // The ref, not the state value, is what the async continuation reads: state
  // captured in the closure is the value at CALL time, which is exactly the
  // stale answer this guard exists to reject.
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MerchantDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [placement, setPlacement] = useState<Record<string, unknown> | null>(null);
  const [placementLoading, setPlacementLoading] = useState(false);
  const activeConnectionRef = useRef<string | null>(null);
  const detailAbortRef = useRef<AbortController | null>(null);
  const placementAbortRef = useRef<AbortController | null>(null);

  // An abort is the caller's own cancellation, never a failure to report: it
  // must not raise a toast or paint an error state.
  const isAbort = (err: unknown) =>
    err instanceof DOMException ? err.name === 'AbortError' : (err as { name?: string })?.name === 'AbortError';

  const authGet = useCallback(async (path: string, signal?: AbortSignal) => {
    const res = await authFetch(`/ops-console/${path}`, { signal });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || body.error || `HTTP ${res.status}`);
    }
    return res.json();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [fleetData, activityData] = await Promise.all([
        authGet(`${PLATFORM}/fleet`),
        authGet(`${PLATFORM}/activity`),
      ]);
      // ⛔ NO SILENT FALLBACK ON THE POPULATIONS (root CLAUDE.md).
      // The selection is set membership over these id lists, and `live` drives
      // the DEFAULT view. Defaulting a missing list to [] would render a
      // confident, fully-populated screen reading "Showing 0 of 22 merchants",
      // every tile zero, MRR €0.00 — with no error anywhere. That is the exact
      // failure mode this console exists to make impossible, so a payload
      // without them is an error, loudly, with the field named.
      const missing = ['liveStores', 'deadConnections', 'dormantStores',
        'untranslatedLanguages', 'translationSurface']
        .filter((k) => !Array.isArray(fleetData?.signals?.[k]));
      if (missing.length > 0) {
        throw new Error(
          `Fleet payload is missing signals.${missing.join(', signals.')} — the console resolves its `
          + 'populations from these lists and will not guess them. The admin build and the backend are '
          + 'probably out of step.'
        );
      }
      // ⛔ SAME RULE FOR THE CONSENT AND GOOGLE COLUMNS (codex #5820 r1, both reviewers).
      // These arrive as `null` when there genuinely is no acceptance / no integration, and the
      // cells render an explicit absent-marker for that. But a payload from an OLDER BACKEND omits
      // the keys entirely — and `undefined` reached the same branch, so every merchant read
      // "No acceptance on file" and "No Google integration", including those who had accepted and
      // were connected. Absent data presented as confirmed absence, on a legal record.
      // Distinguishing the two by presence is what makes the columns trustworthy; `null` is an
      // answer, a missing key is not.
      const rowsMissing = ['termsAcceptance', 'googleIntegration']
        .filter((k) => (fleetData?.merchants ?? []).some((m: Record<string, unknown>) => !(k in m)));
      if (rowsMissing.length > 0) {
        throw new Error(
          `Fleet payload is missing merchants[].${rowsMissing.join(', merchants[].')} — these columns `
          + 'distinguish "no acceptance on file" from "the backend did not say", and will not guess. '
          + 'The admin build and the backend are probably out of step.'
        );
      }
      setFleet(fleetData);
      setActivity(activityData);
    } catch (err) {
      // Real error, shown as an error — never a blank screen pretending to be
      // an empty fleet.
      const message = err instanceof Error ? err.message : String(err);
      setLoadError(message);
      toast({ title: 'Ops Console failed to load', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [authGet]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const closeDetail = useCallback(() => {
    activeConnectionRef.current = null;
    detailAbortRef.current?.abort();
    detailAbortRef.current = null;
    placementAbortRef.current?.abort();
    placementAbortRef.current = null;
    setSelectedConnectionId(null);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(false);
    setPlacement(null);
    setPlacementLoading(false);
  }, []);

  const openDetail = useCallback(async (connectionId: string) => {
    // Cancel everything belonging to the previous selection FIRST — including
    // an outstanding placement check, which would otherwise render inside the
    // newly-opened merchant's drawer.
    detailAbortRef.current?.abort();
    placementAbortRef.current?.abort();
    placementAbortRef.current = null;
    const controller = new AbortController();
    detailAbortRef.current = controller;
    activeConnectionRef.current = connectionId;
    setSelectedConnectionId(connectionId);
    // Cleared at the START of a new selection, so a slow load can never show
    // the previous merchant's data under this merchant's name.
    setDetail(null);
    setDetailError(null);
    setPlacement(null);
    setPlacementLoading(false);
    setDetailLoading(true);
    try {
      const data = await authGet(`${PLATFORM}/merchants/${connectionId}`, controller.signal);
      if (activeConnectionRef.current !== connectionId) return; // superseded or closed
      assertDrilldownShape(data);
      setDetail(data);
    } catch (err) {
      if (isAbort(err)) return;
      if (activeConnectionRef.current !== connectionId) return;
      const message = err instanceof Error ? err.message : String(err);
      // A FAILED LOAD SHOWS AN ERROR, NEVER A FALLBACK. Leaving the previous
      // detail in place would surface another merchant's data as though it
      // were this one's — the worst failure mode on a console whose whole
      // purpose is being trusted.
      setDetail(null);
      setDetailError(message);
      toast({ title: 'Drill-down failed', description: message, variant: 'destructive' });
    } finally {
      if (activeConnectionRef.current === connectionId) setDetailLoading(false);
    }
  }, [authGet]);

  const checkPlacement = useCallback(async (connectionId: string) => {
    placementAbortRef.current?.abort();
    const controller = new AbortController();
    placementAbortRef.current = controller;
    setPlacementLoading(true);
    try {
      const res = await authGet(`${PLATFORM}/merchants/${connectionId}/block-placement`, controller.signal);
      if (activeConnectionRef.current !== connectionId) return; // landed after a switch/close
      setPlacement(res.placement);
    } catch (err) {
      if (isAbort(err)) return;
      if (activeConnectionRef.current !== connectionId) return;
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Block placement check failed', description: message, variant: 'destructive' });
    } finally {
      if (activeConnectionRef.current === connectionId) setPlacementLoading(false);
    }
  }, [authGet]);

  // Nothing may outlive the screen either.
  useEffect(() => () => {
    activeConnectionRef.current = null;
    detailAbortRef.current?.abort();
    placementAbortRef.current?.abort();
  }, []);

  // No `?? []` here: load() refuses a payload whose population lists are
  // absent, so an empty set means an empty fleet — never a missing field.
  const signalSets = useMemo(() => ({
    live: new Set(fleet ? fleet.signals.liveStores : []),
    dead: new Set(fleet ? fleet.signals.deadConnections : []),
    dormant: new Set(fleet ? fleet.signals.dormantStores : []),
    untranslated: new Set(fleet ? fleet.signals.untranslatedLanguages : []),
    // Not a signal you can filter by — the population the untranslated
    // signal ranges over, so its count can carry its denominator (A2.2).
    translationSurface: new Set(fleet ? fleet.signals.translationSurface : []),
  }), [fleet]);

  /** AND semantics: a row must be in the population AND in every active signal set. */
  const matchesSelection = useCallback(
    (r: MerchantRow, pop: Population, sigs: SignalKey[]) =>
      (pop === 'all' || signalSets.live.has(r.connectionId))
      && sigs.every((k) => signalSets[k].has(r.connectionId)),
    [signalSets]
  );

  // The rows the SELECTION admits — the population every tile below counts,
  // and the rows the table shows. One list, so the two cannot disagree.
  const selectedMerchants = useMemo(
    () => (fleet ? fleet.merchants.filter((r) => matchesSelection(r, population, activeSignals)) : []),
    [fleet, population, activeSignals, matchesSelection]
  );

  // Tiles are computed over the SELECTION, from the same per-merchant numbers
  // the server summed for the whole fleet (asserted deep-equal, unfiltered).
  const view = useMemo(
    () => summarize(selectedMerchants, signalSets.live, signalSets.dormant),
    [selectedMerchants, signalSets]
  );

  /**
   * Every chip's count is "how many merchants remain if this chip is applied
   * to the CURRENT selection" — not a static estate-wide tally. A static count
   * beside a filtered view is the contradiction finding 5 recorded: `All (22)`
   * over `5 + 14 + 0`, three overlapping sets, inviting a sum that means
   * nothing.
   *
   * All counts come from ONE pass. Asking each chip separately meant six full
   * scans of the fleet on every render — including renders caused by opening
   * the drawer or raising a toast.
   *
   * `populations` rides along in the same pass, under the same selection, so a
   * chip's "0 of 1" is a fraction of one moment and one selection rather than
   * a live numerator over an estate-wide denominator (finding 13).
   */
  const { chipCounts, chipPopulations } = useMemo(() => {
    const counts: Record<'live' | 'all' | SignalKey, number> = {
      live: 0, all: 0, dead: 0, dormant: 0, untranslated: 0,
    };
    const populations: Record<PopulationIdList, number> = { translationSurface: 0 };
    if (!fleet) return { chipCounts: counts, chipPopulations: populations };
    for (const r of fleet.merchants) {
      const id = r.connectionId;
      const live = signalSets.live.has(id);
      const signalsHold = activeSignals.every((k) => signalSets[k].has(id));
      if (!signalsHold) continue;
      // Switching POPULATION keeps the active signals.
      counts.all += 1;
      if (live) counts.live += 1;
      // ADDING a signal keeps the population; a signal already active is
      // idempotent, so its count is simply the current one.
      if (population === 'all' || live) {
        for (const c of SIGNAL_CHIPS) {
          if (activeSignals.includes(c.key) || signalSets[c.key].has(id)) counts[c.key] += 1;
        }
        if (signalSets.translationSurface.has(id)) populations.translationSurface += 1;
      }
    }
    return { chipCounts: counts, chipPopulations: populations };
  }, [fleet, population, activeSignals, signalSets]);

  const toggleSignal = (key: SignalKey) => setActiveSignals(
    (prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key])
  );

  const selectionIsDefault = population === 'live' && activeSignals.length === 0;

  /**
   * ── AN EMPTY SELECTION IS A DISTINCT STATE (finding 14) ────────────────
   *
   * NOT a population of zero. Rendering every tile as 0 and letting each
   * signal separately announce that nothing is measurable describes THIS
   * SELECTION in sentences that read as facts about the FLEET — the exact
   * confusion between "nothing wrong" and "nothing measured" that killed the
   * trial-expiring signal in PRD v5.1. The screen says it once instead, in
   * one empty state, and shows no figure at all.
   *
   * Reachable in more ways than the chip click that surfaced it: any stack of
   * chips whose intersection is empty lands here, as does a fleet with no live
   * connections on the default view.
   */
  const selectionIsEmpty = selectedMerchants.length === 0;

  const resetSelection = useCallback(() => {
    setPopulation('live');
    setActiveSignals([]);
  }, []);

  const showEveryConnection = useCallback(() => {
    setPopulation('all');
    setActiveSignals([]);
  }, []);

  const visibleMerchants = useMemo(() => {
    // No null-fleet guard: selectedMerchants is already [] before the payload
    // lands, so one could never change the result.
    const rows = selectedMerchants;
    const dir = sortDesc ? -1 : 1;
    const val = (r: MerchantRow): string | number => {
      switch (sortKey) {
        case 'shop': return r.shopDomain ?? '';
        case 'tier': return r.subscription?.tierHandle ?? '';
        case 'price': return r.subscription?.monthlyPriceEur ?? -1;
        case 'installed': return r.connectedAt ?? '';
        default: return r.counts ? r.counts[sortKey] : -1;
      }
    };
    return [...rows].sort((a, b) => {
      const av = val(a); const bv = val(b);
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  }, [selectedMerchants, sortKey, sortDesc]);

  const sortBy = (key: SortKey) => {
    if (sortKey === key) setSortDesc(!sortDesc);
    else { setSortKey(key); setSortDesc(true); }
  };

  const SortableHead = ({ k, children }: { k: SortKey; children: React.ReactNode }) => (
    <TableHead className="cursor-pointer whitespace-nowrap" onClick={() => sortBy(k)}>
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className={`h-3 w-3 ${sortKey === k ? 'opacity-100' : 'opacity-30'}`} />
      </span>
    </TableHead>
  );

  if (loading && !fleet) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin" /> Loading fleet…
      </div>
    );
  }

  if (loadError && !fleet) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Ops Console failed to load
            </CardTitle>
            <CardDescription>{loadError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={load} variant="outline"><RefreshCw className="h-4 w-4 mr-2" />Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!fleet) return null;
  // `overview` is the server's whole-fleet summary — kept so the selection
  // banner can say "N of M" against the estate. Every TILE reads `view`,
  // which is the same arithmetic over the selection.
  const { overview } = fleet;
  // A fleet with no connections at all is the ONE zero on this screen that is
  // a property of the estate rather than of the filters — so it gets its own
  // sentence, and must never be told through the empty-SELECTION one.
  const fleetIsEmpty = fleet.merchants.length === 0;
  // The ONE state whose escape lives elsewhere — the selection-empty card
  // carries it, and picks the destination that actually works. Everywhere
  // else the filter bar keeps its own reset, including the fleet-empty card,
  // which has no escape of its own: a refresh that empties the fleet under an
  // active selection would otherwise leave unclicking each chip as the only
  // way back (review finding, 2026-09-04).
  const escapeLivesInEmptyState = selectionIsEmpty && !fleetIsEmpty;
  // What "Reset to Live" would actually land on: the default view is only an
  // escape from the empty state when live stores exist. Set membership over
  // the server's own id list, like every other population on this screen.
  const liveStoresInFleet = signalSets.live.size;
  // Switching to a population that matches nothing is the same dead end a
  // zero-count chip is, and disabled on the same rule. Computed once so the
  // `disabled` prop and the tooltip that explains it cannot disagree.
  const liveUnavailable = population !== 'live' && chipCounts.live === 0;
  const allUnavailable = population !== 'all' && chipCounts.all === 0;
  // "Now" for cycle position is THE SNAPSHOT'S OWN TIMESTAMP, not the wall
  // clock: the whole screen is one snapshot, so the position in a billing
  // cycle is as of that snapshot too. It also makes the figure reproducible —
  // the same payload always renders the same day-of-cycle.
  const now = new Date(fleet.generated_at).getTime();

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Store className="h-6 w-6" /> Ops Console — {fleet.platformDisplayName}
          </h1>
          <p className="text-sm text-muted-foreground">
            Read-only fleet view · snapshot {fmtDateTime(fleet.generated_at)}
          </p>
        </div>
        <Button onClick={load} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* ── THE SELECTION — it drives every tile below, not just the table ──
           Findings 1, 2, 5 and 10 were one cause with four symptoms: tiles
           counting one population, MRR counting another, chips counting a
           third, and nothing on screen naming any of them. ── */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Population</span>
            {/* ⛔ The SELECTED population is never disabled — `*Unavailable`
                is false for it by construction — which is what keeps every
                state escapable. */}
            <FilterButton
              selected={population === 'live'}
              unavailable={liveUnavailable}
              title={`Connections that are currently installed and active.${liveUnavailable ? ` ${POPULATION_NO_MATCH}` : ''}`}
              ariaLabel={`Live (${chipCounts.live}) — unavailable: ${POPULATION_NO_MATCH}`}
              onClick={() => setPopulation('live')}
              testId="population-live"
            >
              Live ({chipCounts.live})
            </FilterButton>
            <FilterButton
              selected={population === 'all'}
              unavailable={allUnavailable}
              title={`Every connection this platform has ever had, including uninstalled and pending ones.${allUnavailable ? ` ${POPULATION_NO_MATCH}` : ''}`}
              ariaLabel={`All, incl. uninstalled (${chipCounts.all}) — unavailable: ${POPULATION_NO_MATCH}`}
              onClick={() => setPopulation('all')}
              testId="population-all"
            >
              All, incl. uninstalled ({chipCounts.all})
            </FilterButton>

            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground ml-4">
              And
            </span>
            {SIGNAL_CHIPS.map((chip) => {
              const active = activeSignals.includes(chip.key);
              const count = chipCounts[chip.key];
              // ⛔ An ACTIVE chip is never unavailable — see FilterButton.
              const unavailable = !active && count === 0;
              // How many the signal matches ANYWHERE — the size of the id list
              // the server computed, not a second predicate. It is what tells
              // a selection-relative zero from an absolute one, and refusing
              // the click is only honest while the chip still says which it is.
              const fleetWide = signalSets[chip.key].size;
              // A chip WITHOUT a population ranges over the whole selection,
              // where a fraction would be N/N and say nothing (finding 10).
              // An EMPTY selection has no population to state either: `0 of 0
              // · nothing measured` repeated on every chip is precisely the
              // per-signal claim the single empty state replaces (finding 14),
              // and it would be describing the filters while reading as a fact
              // about the fleet.
              const measured = chip.population && !selectionIsEmpty
                ? chipPopulations[chip.population.idList]
                : null;
              // The chip's face, built once so the visible text and the
              // accessible name cannot describe different numbers. The
              // fleet-wide figure appears ONLY when the chip is refused and
              // the two zeroes differ — that is the moment the reader loses
              // the ability to interrogate it by clicking.
              const face = `${chip.label} (${count}${measured === null ? '' : ` of ${measured}`}`
                + `${unavailable && fleetWide > 0 ? ` · ${fleetWide} in the fleet` : ''})`;
              return (
                <FilterButton
                  key={chip.key}
                  selected={active}
                  unavailable={unavailable}
                  title={unavailable
                    ? `${chip.why} ${signalUnavailableWhy(fleetWide)}`
                    : `${chip.why} Filters stack: this narrows the current selection rather than replacing it.`}
                  ariaLabel={`${face} — unavailable: ${signalUnavailableWhy(fleetWide)}`}
                  onClick={() => toggleSignal(chip.key)}
                  className={measured === null ? undefined : 'h-auto flex-col items-start gap-0 py-1.5 text-left'}
                  testId={`chip-${chip.key}`}
                >
                  <span>{face}</span>
                  {/* The population, ON SCREEN — not in a tooltip. Finding 8
                      was meaning that lived only in a title attribute. */}
                  {chip.population && measured !== null && (
                    <span className="text-[10px] font-normal opacity-80" data-testid={`chip-${chip.key}-population`}>
                      {measured === 0 ? chip.population.none : chip.population.are}
                    </span>
                  )}
                </FilterButton>
              );
            })}

            {/* Hidden ONLY while the selection-empty card is up, because that
                card carries the escape and picks a destination that actually
                works — resetting to Live is itself a dead end on a fleet with
                no live stores. Everywhere else, including the fleet-empty
                card, this is the reset. */}
            {!selectionIsDefault && !escapeLivesInEmptyState && (
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto"
                onClick={resetSelection}
              >
                <X className="h-3 w-3 mr-1" />Reset to Live
              </Button>
            )}
          </div>

          {/* The sentence that makes every number below unambiguous — and
              which would be claiming tiles that are not there if the selection
              were empty. The empty state below carries the selection's own
              description in that case, so it is said exactly once. */}
          {!selectionIsEmpty && (
            <p className="text-sm" data-testid="selection-summary">
              <span className="font-semibold">
                Showing {fmtInt(view.stores.total)} of {fmtInt(overview.stores.total)} merchants
              </span>
              <span className="text-muted-foreground">
                {` — ${describeSelection(population, activeSignals)}. Every tile below counts this selection.`}
              </span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* ── AN EMPTY SELECTION, SAID ONCE (finding 14) ───────────────────
           The tiles and the merchant table are REPLACED, not zeroed. Every
           one of them would otherwise render a figure over no merchants —
           `Products 0 across 0 merchants`, `Translated —` with "no merchant
           has an active language", a table saying no merchants match — and a
           reader cannot tell a sentence about this SELECTION from a sentence
           about the FLEET when the screen is full of them. One statement, in
           the place the numbers would have been.

           The activity list below is deliberately NOT gated: it says on its
           own card that it spans all connections regardless of the selection,
           so it is not describing the empty set. ── */}
      {fleetIsEmpty && (
        <Card data-testid="fleet-empty">
          <CardHeader>
            <CardTitle className="text-base">No merchant connections exist yet</CardTitle>
            <CardDescription>
              This platform has no connections at all, so there is nothing for the filters above to
              select from. That is a fact about the fleet — unlike an empty selection, which is a
              fact about the filters.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {!fleetIsEmpty && selectionIsEmpty && (
        <Card data-testid="selection-empty">
          <CardHeader>
            <CardTitle className="text-base">No merchants match this selection</CardTitle>
            {/* ⛔ IT MUST NOT CONTRADICT ITSELF (review finding, 2026-09-04).
                This read `Showing 0 of 22 merchants … No figure is shown for an
                empty selection` — two figures, immediately after promising
                none, on the one card whose entire job is unambiguous copy. The
                fleet's own size is DELIBERATELY still here: it is the fact that
                stops an empty selection reading as an empty estate. What is
                withheld is the per-selection figures, and the sentence now says
                exactly that. */}
            <CardDescription data-testid="selection-empty-detail">
              {`Selection: ${describeSelection(population, activeSignals)}. `
                + `The fleet itself has ${fmtInt(overview.stores.total)} connection`
                + `${overview.stores.total === 1 ? '' : 's'} — the tiles are hidden rather than shown `
                + 'as zero, because a zero here would describe these filters, not the fleet.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* The escape that is GUARANTEED to land somewhere non-empty:
                resetting to Live is only one when live stores exist, which is
                exactly what is false on a fleet whose default view is empty. */}
            {liveStoresInFleet > 0 ? (
              <Button size="sm" variant="outline" onClick={resetSelection} data-testid="selection-empty-escape">
                <X className="h-3 w-3 mr-1" />Reset to Live
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={showEveryConnection} data-testid="selection-empty-escape">
                Show all connections, incl. uninstalled
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!selectionIsEmpty && (
        <>
          {/* ── §5.1 overview tiles — over the SELECTION, each naming its population ── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Card data-testid="tile-stores">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Stores in selection</CardTitle></CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {fmtInt(view.stores.total)}
                  {view.stores.total !== overview.stores.total && (
                    <span className="text-sm text-muted-foreground font-normal"> / {fmtInt(overview.stores.total)} connections</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {sortedStatusEntries(view.stores.byStatus).map(([status, n]) => (
                    <Badge key={status} className={STATUS_BADGE[status] ?? 'bg-gray-100 text-gray-800'}>
                      {status}: {n}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card data-testid="tile-tiers">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Merchants by tier</CardTitle></CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {fmtInt(view.merchantsByTier.withSubscription)}
                  <span className="text-sm text-muted-foreground font-normal"> / {fmtInt(view.merchantsByTier.total)} with a subscription</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {sortedTierEntries(view.merchantsByTier.byTier).map(([tier, n]) => (
                    <Badge key={tier} variant="outline">{tier}: {n}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card data-testid="tile-products">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Products</CardTitle></CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{fmtInt(view.catalog.products)}</div>
                {/* Finding 10: `n / n merchants` is a completeness check that earns
                    its space only when it is NOT n/n. Complete means the population
                    is named and nothing more is claimed. */}
                <p className="text-xs text-muted-foreground">
                  {view.catalog.measuredMerchants === view.catalog.totalMerchants
                    ? <>across {fmtInt(view.catalog.totalMerchants)} merchant{view.catalog.totalMerchants === 1 ? '' : 's'} in selection</>
                    : (
                      <span className="text-amber-600">
                        across {fmtInt(view.catalog.measuredMerchants)} of {fmtInt(view.catalog.totalMerchants)} merchants
                        {' — '}{fmtInt(view.catalog.totalMerchants - view.catalog.measuredMerchants)} did not resolve
                      </span>
                    )}
                </p>
              </CardContent>
            </Card>
            {/* ── The generation FUNNEL (findings 3 + 4) ──
                 Was: title "Generated / Live" over a value of `generated /
                 products`, so the title read as a legend for the two numbers and
                 the first-read conclusion was "25 generated, 1,225 live". Now the
                 title names what the two numbers ARE, and the products
                 denominator moves to the subtext where it still carries the
                 catalogue-coverage fact — the biggest gap on this screen. ── */}
            <Card data-testid="tile-funnel">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Live / Generated</CardTitle></CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <span title="Live (storefront-eligible) is the approved segment by definition — one measure, not two.">
                    {fmtInt(view.catalog.live)}
                  </span>
                  <span className="text-muted-foreground"> / {fmtInt(view.catalog.generated)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {fmtInt(view.catalog.generated)} generated of {fmtInt(view.catalog.products)} products
                  {' · '}
                  <span title="Statuses pending_review and needs_review — awaiting the merchant's review. Counted by the same shared module as every other figure here.">
                    {fmtInt(view.catalog.awaitingReview)} pending review
                  </span>
                  {' · '}{fmtInt(view.catalog.rejected)} rejected
                  {/* Shown only when non-zero: the funnel must ADD UP on screen,
                      and a zero segment adds nothing but noise. */}
                  {view.catalog.inProgress > 0 && <> · {fmtInt(view.catalog.inProgress)} in progress</>}
                  {view.catalog.otherStates > 0 && <> · {fmtInt(view.catalog.otherStates)} other states</>}
                </p>
              </CardContent>
            </Card>
            {/* ── DORMANT, promoted from a chip label to a tile ──
                 Not a defect — the most expensive non-defect on the screen. A
                 dormant merchant looks healthy in every other number here, and is
                 the one who churns at renewal. The chip remains, for acting on
                 it; the tile is what says it matters. ── */}
            <Card data-testid="tile-dormant">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-1"><Moon className="h-4 w-4" />Dormant</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {fmtInt(view.dormancy.dormant)}
                  <span className="text-muted-foreground"> / {fmtInt(view.dormancy.measuredLiveStores)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  live store{view.dormancy.measuredLiveStores === 1 ? ' in selection has' : 's in selection have'} generated nothing
                  {view.dormancy.measuredLiveStores !== view.dormancy.liveStores && (
                    <span className="text-amber-600">
                      {' — '}{fmtInt(view.dormancy.liveStores - view.dormancy.measuredLiveStores)} more could not be measured
                    </span>
                  )}
                </p>
              </CardContent>
            </Card>
            <Card data-testid="tile-translated">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-1"><Languages className="h-4 w-4" />Translated</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <WithDenominator
                    value={view.translation.translated}
                    denominator={view.translation.denominator}
                    absentWhy="No merchant in this selection has an active language — there is no translation surface"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {view.translation.merchantsWithActiveLanguages} / {view.translation.totalMerchants} merchants in selection with active languages
                </p>
              </CardContent>
            </Card>
            <Card data-testid="tile-mrr">
              <CardHeader className="pb-2">
                {/* A3.7: the label names the platform — never a bare "MRR". */}
                <CardTitle className="text-sm font-medium flex items-center gap-1"><Euro className="h-4 w-4" />{fleet.mrr_label}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{fmtEur(view.mrr.amount)}</div>
                <p className="text-xs text-muted-foreground">
                  {view.mrr.payingMerchants} paying merchant{view.mrr.payingMerchants === 1 ? '' : 's'} · {view.mrr.liveConnections} live connection{view.mrr.liveConnections === 1 ? '' : 's'} in selection
                </p>
                {fleet.signals.revenueConcentration && (
                  /* ⛔ THE ONE FIGURE ON THIS SCREEN THAT DOES NOT FOLLOW THE
                     SELECTION, AND IT SAYS SO. Concentration is a fleet-level
                     fact — "the largest payer holds X% of what we earn" — and a
                     share recomputed inside a filtered subset degenerates into
                     noise (filter to one merchant and it is always 100%). So it
                     stays the server's single computation, and the word "fleet"
                     does the work "in selection" does everywhere else. */
                  <p className="text-xs text-muted-foreground mt-1"
                    title="Measured across every live paying merchant, regardless of the selection above — unlike the figures beside it.">
                    Largest payer {fleet.signals.revenueConcentration.shopDomain ?? fleet.signals.revenueConcentration.connectionId}
                    {' '}holds {(fleet.signals.revenueConcentration.share * 100).toFixed(0)}% of fleet MRR
                  </p>
                )}
                {view.mrr.payingSyncFailing > 0 && (
                  /* A3.6: never a confident MRR beside a broken-connection warning
                     about the same merchants without connecting the two. */
                  <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                    <AlertTriangle className="h-3 w-3" />
                    {view.mrr.payingSyncFailing} paying merchant{view.mrr.payingSyncFailing === 1 ? '' : 's'} sync-failing — figure may be stale
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── §5.2 merchant table ── */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Merchants ({fmtInt(visibleMerchants.length)})</CardTitle>
              <CardDescription>
                One row per merchant in the current selection — the same rows the tiles above count.
                Click a row for the drill-down. Numbers come from the same modules the merchant app renders.
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHead k="shop">Store</SortableHead>
                    <TableHead>Status</TableHead>
                    <SortableHead k="installed">Installed</SortableHead>
                    <SortableHead k="tier">Tier</SortableHead>
                    <SortableHead k="price">€/mo</SortableHead>
                    <TableHead title="Subscription row status (for Shopify this is written 'active' unconditionally — the connection status is the discriminator)">Sub status</TableHead>
                    <SortableHead k="products">Products</SortableHead>
                    <SortableHead k="generated">Generated</SortableHead>
                    <SortableHead k="approved">
                      <span title="Approved and storefront-eligible are the SAME measure by definition: eligible = approved/published with no live removal record. There is no approved-but-not-live gap to show.">Approved / live</span>
                    </SortableHead>
                    <SortableHead k="rejected">Rejected</SortableHead>
                    <TableHead>Translated</TableHead>
                    {/* Finding 8: "gap" did not say gap between WHAT, and the
                        row's highest-value signal needed a hover to decode. The
                        second number now names itself in the header. */}
                    <TableHead title="Active languages / of those, how many have zero completed translations.">
                      Langs / untranslated
                    </TableHead>
                    {/* Finding 6: the ratio is billing-cycle-relative and resets
                        invisibly, and every merchant runs on their own clock. The
                        cycle position is on the row now, not only in the drawer. */}
                    <TableHead title="Pages optimized in the CURRENT billing period, against the tier cap. The period is per merchant and the counter resets at its end — the second line says where in that cycle the merchant is.">
                      Optimized / cap
                    </TableHead>
                    {/* Finding 9: `Parked` was a strict SUBSET of this column,
                        rendered as an independent one, with no tooltip at all.
                        Folded in, and named by its cause. */}
                    <TableHead title="Indexed slots held, against the tier cap. The second line is the subset of those slots still held by products that were rejected or removed from the storefront — a rejected product keeps its slot, which is why the cap can be full while fewer products show content.">
                      Slots / cap
                    </TableHead>
                    <TableHead title="Whether this merchant accepted the terms & conditions, and when. Sourced from terms_acceptances — the legal record, not a derived flag. The latest acceptance is shown; the tooltip carries the version they agreed to.">
                      T&amp;C accepted
                    </TableHead>
                    <TableHead title="Google services this store has actually granted, derived from the OAuth scopes on the integration row. An integration that exists but is expired or revoked shows its status instead of the services — that state is actionable, 'not connected' is not.">
                      Google
                    </TableHead>
                    <TableHead>Health</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleMerchants.map((m) => (
                    <TableRow key={m.connectionId} data-testid={`merchant-row-${m.connectionId}`} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(m.connectionId)}>
                      <TableCell className="font-medium whitespace-nowrap">{m.shopDomain ?? <Absent why="Connection has no shop domain recorded" />}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_BADGE[m.connectionStatus] ?? 'bg-gray-100 text-gray-800'}>{m.connectionStatus}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{fmtDate(m.connectedAt) ?? <Absent why="No connected_at timestamp" />}</TableCell>
                      <TableCell>{m.subscription ? m.subscription.tierHandle : <Absent why="No entitled subscription row" />}</TableCell>
                      <TableCell>{m.subscription && m.subscription.monthlyPriceEur !== null ? fmtEur(m.subscription.monthlyPriceEur) : <Absent why="No entitled subscription row" />}</TableCell>
                      <TableCell>{m.subscription ? m.subscription.status : <Absent why="No entitled subscription row" />}</TableCell>
                      <TableCell>{m.counts ? fmtInt(m.counts.products) : <Absent />}</TableCell>
                      <TableCell>{m.counts ? <WithDenominator value={m.counts.generated} denominator={m.counts.products} /> : <Absent />}</TableCell>
                      <TableCell>{m.counts ? <WithDenominator value={m.counts.approved} denominator={m.counts.generated} /> : <Absent />}</TableCell>
                      <TableCell>{m.counts ? <WithDenominator value={m.counts.rejected} denominator={m.counts.generated} /> : <Absent />}</TableCell>
                      <TableCell>
                        <WithDenominator
                          value={m.translated}
                          denominator={m.translationDenominator}
                          absentWhy="No active languages — no translation surface"
                        />
                      </TableCell>
                      <TableCell>
                        {m.activeLocales === null ? <Absent /> : (
                          <span>
                            {m.activeLocales.length}
                            {m.activatedNotTranslated !== null && m.activatedNotTranslated > 0 && (
                              <Badge className="ml-1 bg-amber-100 text-amber-800" title="Active languages with zero completed translations">
                                {m.activatedNotTranslated} untranslated
                              </Badge>
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {m.subscription ? (
                          <>
                            <WithDenominator value={m.subscription.pagesOptimizedThisPeriod} denominator={m.subscription.pagesOptimizedCap} absentWhy="Unlimited tier — no cap" />
                            <CyclePosition
                              start={m.subscription.currentPeriodStart}
                              end={m.subscription.currentPeriodEnd}
                              now={now}
                            />
                          </>
                        ) : <Absent why="No entitled subscription row" />}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {m.indexedSlotsHeld !== null && m.subscription ? (
                          <>
                            <WithDenominator value={m.indexedSlotsHeld} denominator={m.subscription.indexedSlotsCap} absentWhy="Unlimited tier — no cap" />
                            {/* The parked figure is a STRICT SUBSET of the number
                                above it, by construction (same slot fragment plus
                                one predicate) — so it belongs inside this cell,
                                saying what it is a subset OF.
                                ⛔ THREE STATES, NOT TWO. A count that did not
                                resolve must not render the same as a measured
                                zero (A1.2) — the dedicated column this replaced
                                had an <Absent/> and folding it in nearly lost it.
                                Absent shows the em-dash; a measured zero shows
                                nothing, because "no parked slots" is what the
                                unqualified total above already says. */}
                            {m.parkedSlots === null || m.parkedSlots === undefined ? (
                              <div className="text-xs">
                                <Absent why="Parked-slot count did not resolve for this merchant — it is not known to be zero" />
                              </div>
                            ) : m.parkedSlots > 0 ? (
                              <div className="text-xs text-amber-600" title="These slots are held by products that were rejected or removed from the storefront. The slot is not released — that is deliberate — so the cap can be full while fewer products actually show content.">
                                {fmtInt(m.parkedSlots)} held by rejected/removed
                              </div>
                            ) : null}
                          </>
                        ) : <Absent why="No entitled subscription row" />}
                      </TableCell>
                      {/* T&C — the legal answer is WHETHER and WHEN, so the cell leads with the
                          date rather than a tick that would need a hover to be useful. */}
                      <TableCell className="whitespace-nowrap">
                        {m.termsAcceptance ? (
                          <span
                            className="text-muted-foreground"
                            title={`Version ${m.termsAcceptance.termsVersion}`
                              + (m.termsAcceptance.platformUserId ? ` · accepted by ${m.termsAcceptance.platformUserId}` : '')
                              + ` · ${fmtDateTime(m.termsAcceptance.acceptedAt)}`}
                          >
                            {fmtDate(m.termsAcceptance.acceptedAt)}
                          </span>
                        ) : (
                          <Absent why="No acceptance on file for this shop" />
                        )}
                      </TableCell>
                      {/* Google — WHICH services, not merely whether. A store with GSC but no GA4
                          is a different support conversation from one with both. */}
                      <TableCell className="whitespace-nowrap">
                        {!m.googleIntegration || !m.googleIntegration.hasIntegration ? (
                          <Absent why="No Google integration for this site" />
                        ) : !m.googleIntegration.connected ? (
                          <span
                            className="text-amber-600"
                            title={`Google is connected but ${m.googleIntegration.status ?? 'not active'} — reconnect needed.`
                              + (m.googleIntegration.email ? ` Account: ${m.googleIntegration.email}` : '')}
                          >
                            {m.googleIntegration.status ?? 'inactive'}
                          </span>
                        ) : (
                          <span
                            className="text-muted-foreground"
                            title={(m.googleIntegration.email ? `${m.googleIntegration.email} · ` : '')
                              + `connected ${fmtDate(m.googleIntegration.connectedAt) ?? 'date unknown'}`}
                          >
                            {(['gsc', 'ga4', 'ads', 'mc'] as const)
                              .filter((k) => m.googleIntegration!.services[k].connected)
                              .map((k) => ({ gsc: 'GSC', ga4: 'GA4', ads: 'Ads', mc: 'Merchant Center' }[k]))
                              .join(' · ')
                              /* Active integration, zero services granted: real, and it must not
                                 render as an empty cell that reads like "nothing to say". */
                              || <Absent why="Connected, but no service scopes granted" />}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {m.syncFailing ? (
                          <span className="text-red-600 inline-flex items-center gap-1" title={m.lastSyncError ?? undefined}>
                            <AlertTriangle className="h-3 w-3" />
                            {m.lastSyncStatus ?? 'failing'}{m.consecutiveFailures > 0 ? ` ×${m.consecutiveFailures}` : ''}
                          </span>
                        ) : m.lastSyncStatus ? (
                          <span className="text-muted-foreground">{m.lastSyncStatus}</span>
                        ) : (
                          <Absent why="Never synced" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* ── §5.3 activity list (finding 7) ──
           The `ledger` / `derived` tags exposed the storage model instead of
           answering the reader's question, and the deeper risk was
           event-language on rows that cannot support it: `connected_at` and
           `disconnected_at` are single MUTABLE columns — one per connection —
           so "installed the app" claimed a recorded event where only a
           current state exists. The tags are gone; the grammar carries the
           distinction now. State-shaped for connection dates, event-shaped
           for recorded changes, and ONE plain-language note where the tag and
           the history-begins boundary used to be two. ── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4" />Activity</CardTitle>
          <CardDescription data-testid="activity-note">
            {activity?.historyBegins
              ? <>Tier and subscription changes are recorded from {fmtDateMedium(activity.historyBegins)}.</>
              : <>No tier or subscription change has been recorded yet — recording begins with the first one written, so this is not evidence that none have happened.</>}
            {' '}Connection dates show each store&apos;s current state, not a full history.
            {' '}This list spans all connections, regardless of the selection above.
            {' '}{activity && activity.events.length > ACTIVITY_LIMIT
              ? <>Showing the {ACTIVITY_LIMIT} most recent of {fmtInt(activity.events.length)}.</>
              : <>Most recent first.</>}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!activity || activity.events.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing recorded or derivable yet.</p>
          ) : (
            <ul className="space-y-1 text-sm" data-testid="activity-list">
              {activity.events.slice(0, ACTIVITY_LIMIT).map((e, i) => (
                <li key={i} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium">{e.shopDomain ?? e.connectionId}</span>
                  <span className="text-muted-foreground">{describeActivity(e)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ── Alerts (PRD 2026-09-25 §5b) — the same rows the Shopify ops e-mails are built from ── */}
      <OpsAlertsPanel platform={PLATFORM} />

      {/* ── Feed delivery alerts (#7222, webhook PRD rev. 5.2 AC18): the generic store's `delivery` source ── */}
      <OpsAlertsPanel
        platform="delivery"
        title="Feed delivery alerts"
        description="Webhook delivery of the enriched feed: endpoints paused (no success for 24 h, or credential refused) or redirecting, products held by the payload contract check (one alert per schema path, counting what is still held), renders that returned more than one product, runs that failed, and a payload contract that could not be loaded. E-mailed to the operators only — the merchant's tab shows its own status, never these alerts. Read-only."
        kinds={DELIVERY_ALERT_KINDS}
        shopLabel="Store"
        shopPlaceholder="BikeInn"
        testId="delivery-alerts-panel"
      />

      {/* ── §5.5 drill-down panel ── */}
      {selectedConnectionId && (
        <div className="fixed inset-y-0 right-0 w-full md:w-[560px] bg-background border-l shadow-xl z-50 overflow-y-auto p-6 space-y-4"
          data-testid="merchant-drawer" data-connection-id={selectedConnectionId}>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              {detailLoading ? 'Loading…' : detail?.merchant.shopDomain ?? 'Merchant'}
            </h2>
            <Button variant="ghost" size="sm" onClick={closeDetail}><X className="h-4 w-4" /></Button>
          </div>
          {detailError && !detailLoading && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600 text-base">
                  <AlertTriangle className="h-4 w-4" /> Drill-down failed
                </CardTitle>
                <CardDescription data-testid="detail-error">{detailError}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button size="sm" variant="outline" onClick={() => openDetail(selectedConnectionId)}>
                  <RefreshCw className="h-3 w-3 mr-2" />Retry
                </Button>
              </CardContent>
            </Card>
          )}
          {detail && !detailLoading && (
            <>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Subscription</CardTitle></CardHeader>
                <CardContent className="text-sm space-y-1">
                  {detail.merchant.subscription ? (
                    <>
                      <div>Tier: <b>{detail.merchant.subscription.tierName}</b> ({detail.merchant.subscription.tierHandle}) · {detail.merchant.subscription.monthlyPriceEur !== null ? fmtEur(detail.merchant.subscription.monthlyPriceEur) : <Absent />}/mo</div>
                      <div>Period: {fmtDate(detail.merchant.subscription.currentPeriodStart) ?? <Absent />} → {fmtDate(detail.merchant.subscription.currentPeriodEnd) ?? <Absent />}</div>
                      <div>Optimized this period: <WithDenominator value={detail.merchant.subscription.pagesOptimizedThisPeriod} denominator={detail.merchant.subscription.pagesOptimizedCap} absentWhy="Unlimited tier — no cap" /></div>
                    </>
                  ) : <div><Absent why="No entitled subscription row" /> — this merchant has no entitled subscription row.</div>}
                  <div className="pt-1">
                    <span className="text-muted-foreground">History:</span>
                    {detail.subscriptionHistory.length === 0 ? (
                      <span className="ml-1 text-muted-foreground">history has not begun for this merchant</span>
                    ) : (
                      <ul className="list-disc ml-5">
                        {detail.subscriptionHistory.map((h) => (
                          <li key={h.subscriptionId}>
                            {h.tierHandle} ({h.status}) since {fmtDate(h.activatedAt) ?? <Absent />}
                            {h.cancelledAt ? ` · ended ${fmtDate(h.cancelledAt)}` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Translations per locale</CardTitle></CardHeader>
                <CardContent className="text-sm">
                  {detail.merchant.activeLocales && detail.merchant.activeLocales.length > 0 ? (
                    <ul className="space-y-1">
                      {detail.merchant.activeLocales.map((locale) => {
                        const agg = detail.merchant.localeAggregates?.[locale];
                        return (
                          <li key={locale}>
                            <b>{locale}</b>: {agg
                              ? `${agg.completed} completed · ${agg.inFlight} in flight · ${agg.failed} failed${agg.unpublishedCompleted > 0 ? ` · ${agg.unpublishedCompleted} completed-but-unpublished` : ''}`
                              : 'no translation rows yet for this active language'}
                          </li>
                        );
                      })}
                    </ul>
                  ) : <Absent why="No active languages" />}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Removals ({detail.removals.length})</CardTitle></CardHeader>
                <CardContent className="text-sm">
                  {detail.removals.length === 0 ? <span className="text-muted-foreground">No live removal records.</span> : (
                    <ul className="space-y-1">
                      {detail.removals.map((r) => (
                        <li key={r.id}>{fmtDate(r.created_at)} · {r.actor}{r.note ? ` — ${r.note}` : ''}</li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Writeback state</CardTitle>
                  <CardDescription>
                    Failures first, in red. <b>skipped</b> is a deliberate terminal state, not a failed
                    write: cancelled while queued, blocked by the publication gate (rejected, language not
                    active, or plan does not cover it), or a content type we no longer write. Those three
                    share one number here — hover it for what that means before acting on the size of it.
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-sm">
                  {detail.writeback.length === 0 ? <span className="text-muted-foreground">No writeback rows.</span> : (
                    <ul className="space-y-1" data-testid="writeback-states">
                      {orderWriteback(detail.writeback).map((w) => (
                        <li key={`${w.operation}:${w.status}`}
                          data-testid={`writeback-${w.operation}-${w.status}`}
                          data-kind={w.meaning.kind}
                          title={w.meaning.why}
                          className={`flex flex-wrap items-baseline gap-x-1 ${WRITEBACK_KIND_CLASS[w.meaning.kind]}`}>
                          {ALARMING_WRITEBACK_KINDS.includes(w.meaning.kind) && (
                            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                          )}
                          <span>{w.operation} · {w.meaning.label}: {fmtInt(w.count)}</span>
                          <span className="text-muted-foreground text-xs">
                            (last {fmtDateTime(w.lastAttemptedAt) ?? <Absent why="No attempt recorded" />})
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              {/* The aggregate above says HOW MANY failed; this says WHY, in the platform's own
                  words, so the exact error is readable here instead of requiring a database. */}
              {detail.writebackFailures.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Writeback errors</CardTitle>
                    <CardDescription>
                      The exact message the platform returned, newest first. Listed by HAVING an
                      error rather than by status, so a <b>retrying</b> row that is really being
                      rejected appears here too — its aggregate above counts it as in-flight.
                      {detail.writebackFailuresTotal > detail.writebackFailures.length && (
                        <> Showing the {fmtInt(detail.writebackFailures.length)} most recent
                        of {fmtInt(detail.writebackFailuresTotal)}.</>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <ul className="space-y-2" data-testid="writeback-errors">
                      {detail.writebackFailures.map((f, i) => (
                        <li key={`${f.contentType}:${f.attemptedAt}:${i}`}
                          data-testid={`writeback-error-${f.contentType}`}
                          className="border-l-2 border-red-500/60 pl-2">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="font-medium">{f.contentType}</span>
                            <span className={WRITEBACK_KIND_CLASS[f.meaning.kind]} title={f.meaning.why}>
                              {f.operation} · {f.meaning.label}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              {fmtDateTime(f.attemptedAt) ?? <Absent why="No attempt recorded" />}
                            </span>
                          </div>
                          {/* whitespace-pre-wrap + break-words: platform errors carry JSON and
                              schema pointers, and a truncated one sends you back to the database. */}
                          <div className="text-xs text-red-600 whitespace-pre-wrap break-words">
                            {f.errorMessage}
                          </div>
                          {f.platformEntityId && (
                            <div className="text-muted-foreground text-xs">{f.platformEntityId}</div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Bulk activation jobs</CardTitle>
                  <CardDescription data-testid="bulk-jobs-description">
                    Products published, out of the products the job could publish — products removed from
                    the storefront are excluded, so that denominator can be smaller than the job's own
                    source total, and any job where they differ says so on its own line.
                    Publication-gate figures count content ITEMS — one product publishes several — so they
                    sit on their own line and are never a fraction of the job.
                    {' '}
                    {detail.bulkActivationJobsTotal > detail.bulkActivationJobs.length
                      ? (
                        <span data-testid="bulk-jobs-truncation">
                          Showing the {detail.bulkActivationJobs.length} most recent
                          of {fmtInt(detail.bulkActivationJobsTotal)}.
                        </span>
                      )
                      : <span data-testid="bulk-jobs-truncation">Most recent first.</span>}
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-sm">
                  {detail.bulkActivationJobs.length === 0 ? <span className="text-muted-foreground">No bulk activation jobs.</span> : (
                    <ul className="space-y-2" data-testid="bulk-activation-jobs">
                      {detail.bulkActivationJobs.map((j) => (
                        <BulkActivationJobLine key={j.id} job={j} />
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              {/* PRD 2026-09-25 §5b: this merchant's alerts, last 30 days — keyed by connection so a new
                  selection remounts (and aborts) the request instead of rendering another merchant's list. */}
              {/* PRD 2026-10-08 §6.4: this merchant's batches and the operator re-run — a sibling endpoint, keyed by
                  connection for the same reason as the alerts panel. */}
              <OpsBatchesPanel key={`batches-${detail.merchant.connectionId}`} platform={PLATFORM} connectionId={detail.merchant.connectionId} />

              <OpsAlertsPanel key={detail.merchant.connectionId} platform={PLATFORM} connectionId={detail.merchant.connectionId} />

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Chatbot sessions (last {detail.dailyUsage?.length ?? 0} days recorded)</CardTitle></CardHeader>
                <CardContent className="text-sm">
                  {detail.dailyUsage.length === 0 ? <span className="text-muted-foreground">No usage rows.</span> : (
                    <ul className="space-y-1">
                      {detail.dailyUsage.map((d) => (
                        <li key={`${d.usage_date}-${d.tier_handle}`}>
                          {fmtDate(d.usage_date)}: {d.chatbot_sessions_count} sessions
                          <span className="text-muted-foreground"> · on {d.tier_handle}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-1"><Blocks className="h-4 w-4" />Block placement</CardTitle>
                  <CardDescription>Live check against the merchant's theme — computed on demand, never cached, never on the fleet table.</CardDescription>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  <Button size="sm" variant="outline" disabled={placementLoading}
                    onClick={() => checkPlacement(detail.merchant.connectionId)}>
                    {placementLoading ? <RefreshCw className="h-3 w-3 mr-2 animate-spin" /> : null}
                    Check placement now
                  </Button>
                  {placement && (
                    <pre className="text-xs bg-muted rounded p-2 overflow-x-auto">{JSON.stringify(placement, null, 2)}</pre>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
